import { User } from 'app/constants/schemas';
import { IBookingInterface } from 'app/containers/rtc/BookingContext';
import { getAuthUser } from 'app/models/auth/selectors';
import { IBookingTuple } from 'app/models/rtc/booking/constants';
import { debug } from 'app/services/logger';
import { AppState } from 'app/store';
import {
  useEffect,
  useRef,
  FunctionComponent,
  ReactNode, ReactElement,
} from 'react';
import { connect } from 'react-redux';
import { useBookingQuery } from 'app/containers/rtc/BookingContext/BookingQueryContext';

/**
 * Handles loading a booking from history parameters or based on session state.
 */
interface IProps {
  activeBooking?: IBookingTuple;
  bookingInterface: IBookingInterface;
  user?: User;
  children?: ReactNode;
}

const BookingAutoload: FunctionComponent<IProps> = ({
  activeBooking,
  bookingInterface,
  children = null,
  user,
}) => {
  const mounted = useRef<boolean>(false);

  const [queryBooking] = useBookingQuery();

  const preferredCallParams = 'bookingId' in queryBooking ? queryBooking : activeBooking;

  const userId = user && user.id;

  // If the user logs out, clean up the old booking details
  useEffect(() => {
    if (mounted.current) {
      debug('User no longer present, clearing active booking.');
      bookingInterface.clearBookings();
    }
  }, [userId, mounted, bookingInterface]);

  // Load new booking if booking information in the query params is different than
  // the active booking.
  useEffect(() => {
    if (
      preferredCallParams &&
      (
        // Load the booking if the component hasn't mounted  yet
        !mounted.current ||
        // Load the booking the active booking differs from the query booking
        !activeBooking ||
        activeBooking.bookingType !== preferredCallParams.bookingType ||
        activeBooking.bookingId !== preferredCallParams.bookingId
      )
    ) {
      debug('Autoloading booking.', preferredCallParams);
      const { bookingType, bookingId, hostKey } = preferredCallParams;
      bookingInterface.fetchBooking(bookingType, bookingId, hostKey);
    }
  }, [bookingInterface, activeBooking, preferredCallParams]);

  useEffect(() => {
    mounted.current = true;
  }, []);

  return children as ReactElement; // Work around broken React typing
};

const BookingAutoloadInjectDependencies = connect(
  (state: AppState) => ({
    user: getAuthUser(state),
  }),
)(BookingAutoload);

export default BookingAutoloadInjectDependencies;
